import { File, Folder, Resource } from '@nextforce/api'
import { makeStyles, Text } from '@rneui/themed'
import { Image } from 'expo-image'
import { FileFolderSortOption } from 'file_folder_repository/types'
import {
  getFileSize,
  getFriendlyMimeType,
  getPathName,
  getTimeFromNow,
  isVideoType,
} from 'file_folder_repository/util'
import { t } from 'app/i18l'
import { useMemo } from 'react'
import { StyleProp, StyleSheet, TextStyle, View, ViewStyle } from 'react-native'
import { PressableOpacity } from 'shared/components/pressable_opacity'
import { getDarkShadeColor, getLightShadeColor } from 'shared/utils'
import { FileFolderIcon } from './file_folder_icon'

const FILE_FOLDER_THUMBNAIL_SIZE = 60

export function FolderListItem({
  folder,
  onPress,
  onLongPress,
  showPath = false,
  metaDataType = 'dateDesc',
  descriptionStyle,
  infoContainerStyle,
  style,
  thumbnailHeight = FILE_FOLDER_THUMBNAIL_SIZE,
  thumbnailWidth = FILE_FOLDER_THUMBNAIL_SIZE,
  thumbnailIconSize,
  titleStyle,
  showUploadedBy = false,
  titleDescriptionContainer,
}: {
  folder: Folder
  onPress: (folder: Folder) => void
  onLongPress: (folder: Folder) => void
  showPath?: boolean
  metaDataType?: FileFolderSortOption | 'none'
  style?: StyleProp<ViewStyle>
  titleStyle?: StyleProp<TextStyle>
  descriptionStyle?: StyleProp<TextStyle>
  infoContainerStyle?: StyleProp<ViewStyle>
  thumbnailHeight?: number
  thumbnailWidth?: number
  thumbnailIconSize?: number
  showUploadedBy?: boolean
  titleDescriptionContainer?: StyleProp<ViewStyle>
}) {
  const styles = useStyles()
  const pathName = getPathName(folder.path)
  return (
    <PressableOpacity
      onPress={() => onPress(folder)}
      onLongPress={() => onLongPress(folder)}
      onContextMenu={e => {
        e.preventDefault()
        onLongPress(folder)
      }}
    >
      <View style={[styles.fileFolderListItemContainer, style]}>
        <FileFolderIcon
          resource={folder}
          size={thumbnailIconSize}
          style={{
            ...styles.fileFolderListIcon,
            height: thumbnailHeight,
            width: thumbnailWidth,
          }}
        />
        <View style={[styles.infoContainer, infoContainerStyle]}>
          <View
            style={[
              styles.titleDescriptionContainer,
              titleDescriptionContainer,
            ]}
          >
            <Text
              numberOfLines={folder.description ? 1 : 2}
              style={[styles.fileFolderName, titleStyle]}
            >
              {folder.name}
            </Text>
            {folder.description ? (
              <Text
                numberOfLines={2}
                style={[styles.description, descriptionStyle]}
              >
                {folder.description}
              </Text>
            ) : null}
          </View>
          {showUploadedBy ? (
            <Text
              style={styles.fileTypeText}
            >{`Uploaded by ${folder.createdBy?.firstName} ${folder.createdBy?.lastName}`}</Text>
          ) : null}
          {showPath ? <Text>{pathName}</Text> : null}
          <MetadataText option={metaDataType} item={folder} />
        </View>
      </View>
    </PressableOpacity>
  )
}

export function FileListItem({
  file,
  onPress,
  showPath = false,
  onLongPress,
  style,
  titleStyle,
  infoContainerStyle,
  descriptionStyle,
  thumbnailHeight = FILE_FOLDER_THUMBNAIL_SIZE,
  thumbnailWidth = FILE_FOLDER_THUMBNAIL_SIZE,
  metaDataType = 'dateDesc',
  thumbnailIconSize = 30,
  showUploadedBy = false,
  titleDescriptionContainerStyle,
  disableTouch,
}: {
  file: File
  onPress: (file: File) => void
  onLongPress: (file: File) => void
  showPath?: boolean
  style?: StyleProp<ViewStyle>
  titleStyle?: StyleProp<TextStyle>
  descriptionStyle?: StyleProp<TextStyle>
  infoContainerStyle?: StyleProp<ViewStyle>
  thumbnailHeight?: number
  thumbnailWidth?: number
  thumbnailIconSize?: number
  metaDataType?: FileFolderSortOption | 'none'
  showUploadedBy?: boolean
  titleDescriptionContainerStyle?: StyleProp<ViewStyle>
  disableTouch?: boolean
}) {
  const styles = useStyles()
  const pathName = getPathName(file.path)

  return (
    <PressableOpacity
      disabled={disableTouch}
      onPress={() => onPress(file)}
      onLongPress={() => onLongPress(file)}
      onContextMenu={e => {
        e.preventDefault()
        onLongPress(file)
      }}
      style={styles.container}
    >
      <View style={[styles.fileFolderListItemContainer, style]}>
        {file.currentVersion?.thumbnailUrl ? (
          <View
            style={[styles.fileFolderListIcon, styles.fileListImageContainer]}
          >
            <Image
              source={{ uri: file.currentVersion?.thumbnailUrl }}
              style={[
                styles.fileListImage,
                {
                  width: thumbnailWidth,
                  minHeight: thumbnailHeight,
                },
              ]}
              contentFit="cover"
            />
            {isVideoType(file.currentVersion?.fileMimeType) ? (
              <View style={styles.videoIconContainer}>
                <Image
                  style={styles.videoIcon}
                  source={require('assets/chat/play.png')}
                  contentFit="contain"
                />
              </View>
            ) : null}
          </View>
        ) : (
          <FileFolderIcon
            resource={file}
            size={thumbnailIconSize}
            style={{
              ...styles.fileFolderListIcon,
              height: thumbnailHeight,
              width: thumbnailWidth,
            }}
          />
        )}
        <View style={[styles.infoContainer, infoContainerStyle]}>
          <View
            style={[
              styles.titleDescriptionContainer,
              titleDescriptionContainerStyle,
            ]}
          >
            <Text
              numberOfLines={file.description ? 1 : 2}
              style={[styles.fileFolderName, titleStyle]}
            >
              {file.name}
            </Text>
            {file.description ? (
              <Text
                numberOfLines={2}
                style={[styles.description, descriptionStyle]}
              >
                {file.description}
              </Text>
            ) : null}
          </View>
          {showUploadedBy ? (
            <Text
              style={styles.fileTypeText}
            >{`Uploaded by ${file.currentVersion?.uploadedBy?.firstName} ${file.currentVersion?.uploadedBy?.lastName}`}</Text>
          ) : null}
          {showPath ? <Text>{pathName}</Text> : null}
          <MetadataText option={metaDataType} item={file} />
        </View>
      </View>
    </PressableOpacity>
  )
}

function MetadataText({
  option,
  item,
}: {
  option: FileFolderSortOption | 'none'
  item: Resource
}) {
  const styles = useStyles()

  const text = useMemo(() => {
    switch (option) {
      case 'sizeDesc':
        if (item.type === 'file') {
          if (item.currentVersion?.externalUrl) {
            return t('webDocument')
          }
          return `${getFriendlyMimeType(item.currentVersion?.fileMimeType)}${
            item.currentVersion?.size
              ? ` ∙ ${getFileSize(item.currentVersion.size)}`
              : ''
          }`
        }
        break
      default:
        if (item.updated) {
          return getTimeFromNow(item.updated)
        }
        if (item.created) {
          return getTimeFromNow(item.created)
        }
    }
  }, [item, option])

  if (!text || option === 'none') {
    return null
  }

  return <Text style={styles.fileTypeText}>{text}</Text>
}

const useStyles = makeStyles(theme => ({
  container: { flex: 1 },
  fileFolderListItemContainer: {
    padding: 10,
    backgroundColor:
      theme.mode === 'dark' ? theme.colors.grey11 : getLightShadeColor(0.5),
    flex: 1,
    marginHorizontal: 5,
    marginVertical: 2,
    flexDirection: 'row',
    borderRadius: 10,
    alignItems: 'center',
  },
  fileFolderListIcon: {
    backgroundColor:
      theme.mode === 'dark' ? getDarkShadeColor(0.3) : getDarkShadeColor(0.05),
    padding: 15,
    borderRadius: 5,
    justifyContent: 'center',
    alignItems: 'center',
  },
  fileListImageContainer: {
    padding: 0,
    backgroundColor:
      theme.mode === 'dark' ? theme.colors.grey10 : theme.colors.white,
  },
  fileListImage: { borderRadius: 5, flex: 1 },
  fileFolderName: { fontSize: 16, marginBottom: 5 },
  description: { flex: 1 },
  fileTypeText: {
    fontSize: 12,
    color: theme.colors.grey3,
    lineHeight: 20,
  },
  infoContainer: { flex: 1, marginLeft: 10 },
  titleDescriptionContainer: { justifyContent: 'space-between', flex: 1 },
  videoIcon: {
    height: 20,
    width: 20,
  },
  videoIconContainer: {
    ...StyleSheet.absoluteFillObject,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: getDarkShadeColor(),
    borderRadius: 5,
  },
}))
